import { useSelector } from 'react-redux';
import { DealerLabel, FormTitle, LabeledTextField, PhoneInputField, ReadOnlyField, ScrollContainer } from "./components";
import Grid from '@mui/material/Grid2';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { useEffect, useState } from 'react';
import Spinner from '../spinner/Spinner';
import GridSpacer from './components/gridSpacer';
import { useModelState, useNotification } from '../../hooks';
import { useTechAssistAddStarterData, useTechAssistSerialDetails } from '../../fetch/fetchTechAssist';
import { apiHostUrl } from '../../helpers';
import { getAuthenticatedHeaders } from '../../fetch/fetchHeaders';
import LabeledSelectField from './components/labeledSelectField';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { logFeatureUsage } from '../../fetch/insightsHelper';

export default function TechnicalAssistancePage() {
    const navigate = useNavigate();
    const isPermissionsLoaded = useSelector((state) => state.permissions.isLoaded);
    const hasTechAssist = useSelector((state) => state.permissions.hasTechAssist);

    useEffect(() => {
        if (isPermissionsLoaded && !hasTechAssist) {
            navigate('/');
        }
    }, [isPermissionsLoaded, hasTechAssist]);

    const { showErrorModal } = useNotification();
    const [saving, setSaving] = useState(false);
    const { serialNumber } = useParams();
    const { data: serialDetails } = useTechAssistSerialDetails(serialNumber);
    const { data, isLoading } = useTechAssistAddStarterData(serialNumber);
    const accountNumber = useSelector((state) => state.permissions.accountNumber);


    useEffect(() => {
        document.title = 'Mercury Literature - Tech Assistance Request';
        logFeatureUsage("TechAssistCreate");
    }, []);

    const [model, handleValueChanged, setModelState] = useModelState({});
    useEffect(() => {
        if (!isLoading && !!data) {
            setModelState(data);
        }
    }, [isLoading, JSON.stringify(data)]);

    const hasRequiredFields = () => {
        const { contactFirstName, contactLastName, problemDescription,
            actionRequested, consumerFirstName, consumerLastName, contactAreaCode, contactPhone } = model;

        return !!contactFirstName && !!contactLastName && !!problemDescription &&
            !!actionRequested && !!consumerFirstName && !!consumerLastName &&
            model.problemDescription.length >= 50 && !!contactAreaCode && !!contactPhone;
    }

    const isValid = !saving && !isLoading && hasRequiredFields();

    const handleSubmitClick = () => {
        setSaving(true);
        getAuthenticatedHeaders({ 'Content-Type': 'application/json' }).then((headers) => {
            fetch(`${apiHostUrl}/api/v1/techassist/add`,
                {
                    method: 'POST',
                    body: JSON.stringify(model),
                    headers
                })
                .then((response) => {
                    if (!response.ok) {
                        console.log(response);
                        showErrorModal('Error submitting tech assistance request');
                    } else {
                        setModelState({});
                        window.location.replace('/literature/techAssist')
                    }
                }, (error) => {
                    showErrorModal('Error submitting tech assistance request');
                    console.error('Error submitting tech assistance request', error);
                }).finally(() => {
                    setSaving(false);
                });
        });
    }

    const actionRequestedOptions = [
        { id: '', label: 'Select One' },
        { id: 'Warranty/MPP/PPP Preauthorization', label: 'Warranty/MPP/PPP Preauthorization' },
        { id: 'Technical Advice', label: 'Technical Advice' },
        { id: 'Information Only', label: 'Information Only' },
        { id: 'Other', label: 'Other' }
    ];

    return (
        <ScrollContainer>
            <Grid container spacing={2}>
                <FormTitle title="Tech Assistance Request Form" />
                {!!data ? (
                    <>
                        <DealerLabel accountNumber={accountNumber} />
                        <ReadOnlyField
                            title="Request Status"
                            value="Open"
                        />
                        <ReadOnlyField
                            title="Serial Number"
                            value={serialNumber}
                        />
                        {!!model?.modelNumber ? <ReadOnlyField
                            title="Model Number"
                            value={model?.modelNumber}
                        /> : null}
                        {!!serialDetails?.modelDescription ? <ReadOnlyField
                            title="Model Description"
                            value={serialDetails?.modelDescription}
                        /> : null}
                        {!!serialDetails?.standardWarrantyStartDate && !!serialDetails?.standardWarrantyEndDate ? <ReadOnlyField
                            title="Standard Warranty Period"
                            value={`${serialDetails?.standardWarrantyStartDate ?? ''}${!!serialDetails?.standardWarrantyStartDate && !!serialDetails?.standardWarrantyEndDate ? ' to ' : ''}${serialDetails?.standardWarrantyEndDate ?? ''}`}
                        /> : null}
                        {!!serialDetails?.customerFirstName && !!serialDetails?.customerLastName ? <ReadOnlyField
                            title="Registered Customer Name"
                            value={`${serialDetails?.customerFirstName ?? ''}${!!serialDetails?.customerFirstName && !!serialDetails?.customerLastName ? ' ' : ''}${serialDetails?.customerLastName ?? ''}`}
                        /> : null}
                        {!!serialDetails?.bulletinNumbers && serialDetails?.bulletinNumbers.length > 0 ? (
                            <Grid size={6}>
                                <div style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '1rem', lineHeight: '1.4375em', transform: 'translate(0, -1.5px) scale(0.75)', transformOrigin: 'top left' }}>Service Bulletin{serialDetails?.bulletinNumbers.length > 1 ? 's' : ''}</div>
                                {(serialDetails?.bulletinNumbers || []).map(({ partNumber, displayPartNumber, title }) => (
                                    <div key={partNumber}>
                                        <a href={`/literature/view/${partNumber}`} target="_blank" rel="noopener noreferrer">
                                            {title} ({displayPartNumber}) <OpenInNewIcon fontSize="small" />
                                        </a>
                                    </div>
                                ))}
                            </Grid>
                        ) : null}
                        <GridSpacer size={{ xs: 12 }} />
                        <LabeledTextField
                            label="Tech Id"
                            value={model?.techID || ''}
                            onChange={(e) => handleValueChanged('techID', e.target.value)}
                            maxLength={10}
                            size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}
                        />
                        <LabeledTextField
                            label="Contact Email"
                            value={model?.contactEmail || ''}
                            onChange={(e) => handleValueChanged('contactEmail', e.target.value)}
                            maxLength={250}
                            size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}
                        />
                        <GridSpacer size={{ xs: 0, sm: 0, md: 4, lg: 6, xl: 8 }} />
                        <LabeledTextField
                            label="Contact First Name"
                            value={model?.contactFirstName || ''}
                            onChange={(e) => handleValueChanged('contactFirstName', e.target.value)}
                            maxLength={14}
                            error={!model?.contactFirstName}
                            helperText={!model?.contactFirstName ? 'Contact First Name is required' : null}
                            size={{ xs: 12, sm: 6, md: 3, lg: 3, xl: 2 }}
                        />
                        <LabeledTextField
                            label="Contact Last Name"
                            value={model?.contactLastName || ''}
                            onChange={(e) => handleValueChanged('contactLastName', e.target.value)}
                            maxLength={20}
                            error={!model?.contactLastName}
                            helperText={!model?.contactLastName ? 'Contact Last Name is required' : null}
                            size={{ xs: 12, sm: 6, md: 3, lg: 3, xl: 2 }}
                        />
                        <PhoneInputField
                            model={model}
                            handleValueChanged={handleValueChanged}
                            size={{ xs: 12, sm: 10, md: 6, lg: 5, xl: 4 }}
                        />
                        <LabeledTextField
                            label="Description of Problem"
                            value={model?.problemDescription || ''}
                            onChange={(e) => handleValueChanged('problemDescription', e.target.value)}
                            multiline
                            minRows={2}
                            error={!model?.problemDescription || model.problemDescription.length < 50}
                            helperText={!model?.problemDescription || model.problemDescription.length < 50 ? 'Description of the problem must be at least 50 characters.' : ''}
                        />
                        <LabeledTextField
                            label="List Diagnosis"
                            value={model?.diagnosis || ''}
                            onChange={(e) => handleValueChanged('diagnosis', e.target.value)}
                            multiline
                            minRows={2}
                        />
                        <LabeledTextField
                            label="Suspected Cause of Failure"
                            value={model?.causeOfFailure || ''}
                            onChange={(e) => handleValueChanged('causeOfFailure', e.target.value)}
                            multiline
                            minRows={2}
                        />
                        <LabeledTextField
                            label="List Repairs Already Performed and Parts Replaced"
                            value={model?.repairsAlreadyPerformed || ''}
                            onChange={(e) => handleValueChanged('repairsAlreadyPerformed', e.target.value)}
                            multiline
                            minRows={2}
                        />
                        <LabeledSelectField
                            label="Action Requested"
                            value={model?.actionRequested || ''}
                            onChange={(e) => handleValueChanged('actionRequested', e.target.value)}
                            size={{ xs: 12, md: 6, lg: 5, xl: 4 }}
                            options={actionRequestedOptions}
                            error={!model?.actionRequested}
                            helperText={!model?.actionRequested ? 'Action Requested is required' : null}
                        />
                        <LabeledTextField
                            label='Explanation for "Other" Action Requested'
                            value={model?.explanationActionRequested || ''}
                            onChange={(e) => handleValueChanged('explanationActionRequested', e.target.value)}
                            multiline
                            minRows={2}
                        />
                        <LabeledTextField
                            label="Consumer First Name"
                            value={model?.consumerFirstName || ''}
                            onChange={(e) => handleValueChanged('consumerFirstName', e.target.value)}
                            maxLength={14}
                            size={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 2 }}
                            error={!model?.consumerFirstName}
                            helperText={!model?.consumerFirstName ? 'Consumer First Name is required' : null}
                        />
                        <LabeledTextField
                            label="Consumer Middle Initial"
                            value={model?.consumerMiddleName || ''}
                            onChange={(e) => handleValueChanged('consumerMiddleName', e.target.value)}
                            maxLength={1}
                            size={{ xs: 12, sm: 4, md: 3, lg: 2 }}
                        />
                        <LabeledTextField
                            label="Consumer Last Name"
                            value={model?.consumerLastName || ''}
                            onChange={(e) => handleValueChanged('consumerLastName', e.target.value)}
                            maxLength={18}
                            size={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 2 }}
                            error={!model?.consumerLastName}
                            helperText={!model?.consumerLastName ? 'Consumer Last Name is required' : null}
                        />
                        <GridSpacer size={{ xs: 0, sm: 0, md: 3, lg: 4, xl: 6 }} />
                        <Grid size={12}>
                            <Button disabled={!isValid} onClick={handleSubmitClick}>Submit Request</Button>
                        </Grid>
                    </>
                ) : <Spinner />}
            </Grid>
        </ScrollContainer>
    );
}